<template>
    <div class="article-blog-page" @scroll="scrolling()">
        <breadcrumbs
            :breadcrumbs="breadcrumbs"
            :scroll="scroll"
            @goBack="back"
        />
        <section class="article-blog__banner article-blog__banner--eclipse">
            <img
                class="article-blog__banner-img"
                src="@/assets/blog/why-ai-agents-break-in-production.jpg"
                alt="Why AI agents break in production"
                loading="lazy"
                modernize='sizes=["768w", "1200w", "1536w", "1920w", "2400w", "3840w", "original"] imageFormats=["webp", "original"]'
            />
            <h1 class="article-blog__banner-title">
                Why AI Agents Break In Production: Anatomy Instead Of Magic
            </h1>
        </section>
        <div class="container article-blog section_holder">
            <div class="section_title">
                <SidebarBlog :tagFromPost="tags" @searchByTag="goToPath"/>
            </div>
            <div class="section_content">
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        The problem
                    </h2>
                    <p>
                        You can build an AI agent in five minutes today: three lines of code, and it answers, calls tools, and looks smart. Production is a different story. A $50 pilot, once it hits real traffic, can turn into a production bill of <a href="https://www.softed.com/softed-blog/why-your-ai-pilot-cost-50-and-your-production-bill-will-cost-847000" target="_blank" rel="noopener">$847,000</a>; a coding agent ignored an explicit ALL-CAPS "code freeze," <a href="https://dev.to/utibe_okodi_339fb47a13ef5/the-7-ai-agent-failures-youll-never-see-coming-until-they-hit-production-fg8" target="_blank" rel="noopener">deleted a live database of 1,206 records and then fabricated around 4,000 fake user profiles to cover it up</a>; and in one well-known case an AI agent ran <b>terraform destroy</b> and wiped an entire production environment along with two years of student work.
                    </p>
                    <p>
                        These aren't edge cases: in July 2025 Gartner predicted that over 40% of agent projects will be cancelled by 2027, citing runaway costs, unclear business value, and weak risk control. Here's the key point: it's not the models. GPT, Claude, and Gemini work great. What breaks is everything built around them.
                    </p>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        Two ways to approach it
                    </h2>
                    <p>
                        Anthropic made the core point back in 2024: the most successful teams didn't reach for complex frameworks or specialized libraries, they assembled agents out of simple, composable patterns (see <a href="https://www.anthropic.com/research/building-effective-agents" target="_blank" rel="noopener">Building effective agents</a>). In other words, the question isn't which framework to pick, it's understanding what an agent is actually made of.
                    </p>
                    <p>
                        Dex from HumanLayer reached the same conclusion after interviewing about a hundred founders and writing up the <a href="https://github.com/humanlayer/12-factor-agents" target="_blank" rel="noopener">"12-Factor Agents"</a> methodology: most working production products turned out to be not magical autonomous loops but ordinary, well-written software with LLM calls dropped in carefully and only where they earn their place.
                    </p>
                    <p>
                        You can go the other way and grab a ready-made framework (LangChain, CrewAI, AutoGen, OpenAI Agents SDK, Google ADK). The start will be fast, but the framework hides exactly the parts that later break: input validation, loop limits, tool error handling. The principle Anthropic, OpenAI, and Google all agree on is simple: take the simplest solution and add complexity only when the data proves the simple one isn't enough. Once you understand an agent's anatomy, it stops mattering what's under the hood, a framework or plain Python.
                    </p>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        How we build production agents for ourselves and our clients
                    </h2>
                    <p>
                        Under every pattern below sits the same idea: less magic, more engineering. Here's how we work through it, step by step.
                    </p>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        Step 1. Decide whether the client even needs an agent
                    </h2>
                    <p>
                        First we decide whether the client even needs an agent. Here we follow the same line as Anthropic, OpenAI, and Google: there are two kinds of systems. A <b>workflow</b> works like this: we as developers lay out the path in code (step one, then two, then three), and the model just executes each step without choosing the next one. An <b>agent</b> is different: the LLM itself decides what to do next, which tools to call, and in what order.
                    </p>
                    <p>
                        There's a whole spectrum between them, and every move toward autonomy costs more: more money, more errors, less predictability. In our experience, most client tasks are solved with workflow patterns, not real agents. So by default we choose less autonomy, and we don't sell a client autonomy for its own sake.
                    </p>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        Step 2. Climb the complexity ladder only when you hit a wall
                    </h2>
                    <p>
                        We climb the complexity ladder only when we hit a wall. We start with the simplest thing, a single LLM call. If "one input, one output" is enough for the task, that's where we stop, no extra layers. If one call isn't enough, we move to a chain, where the output of one step becomes the input to the next. Next comes routing: a fast, cheap classifier model sends the request down the right branch. Then parallel execution of independent subtasks. Then the "orchestrator and workers" pattern, where the model breaks the task down itself but we define the set of workers. And only at the very top do you find a truly autonomous ReAct agent and multi-agent systems.
                    </p>
                    <p>
                        Each rung answers a specific limitation of the one below it, and each is more expensive to build, maintain, and run. Here's what we've learned on projects: a single agent with simple tools often beats a complex multi-agent system. That's how Claude Code works, getting by with grep, bash, and files, no vector databases at all, and that benchmark is usually enough for us.
                    </p>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        Step 3. Write the system prompt as an architectural contract
                    </h2>
                    <p>
                        We write the system prompt as an architectural contract. It's not a line of text scribbled together in five minutes. It's the foundation of the agent's entire behavior, and we treat it like code: we version it, review it, test it, and keep it with a change history, not in a text file on someone's desktop. We start with at least four sections.
                    </p>
                    <ul>
                        <li><b>Role and boundaries</b> — what the agent does and, crucially, what it does not do (otherwise it'll start opining on the weather and the meaning of life).</li>
                        <li><b>Rules, numbered by priority</b> — because safety beats budget and budget beats convenience. Without priorities the model decides for itself, and almost certainly not the way the client wants.</li>
                        <li><b>The response format</b> — or you'll get a wall of Markdown or invalid JSON.</li>
                        <li><b>An example</b> — at least one finished dialog right there in the prompt. A concrete sample sets behavior more reliably than any abstract rules.</li>
                    </ul>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        Step 4. Assemble the pipeline on "block early, spend late"
                    </h2>
                    <p>
                        We assemble the pipeline on a "block early, spend late" principle. Under the hood of any pattern, every request passes through a conveyor of stages. We put the cheap checks first and the expensive model call last. Our usual order: input validation (empty string, garbage, unsupported language, and an immediate stop, zero cents spent), input security (a prompt-injection check before any costly operation), enriching the request with context, pulling in knowledge and memory, filtering the retrieval results, assembling the prompt, generation, an output security check (did any personal data or the system prompt leak), and formatting the answer with mandatory JSON validation.
                    </p>
                    <p>
                        We explain the why to clients with simple arithmetic. If 5% of a million monthly requests are junk, then one <b>if</b> with a regex at the validation stage saves around $2,500 that would otherwise be spent running garbage through the whole pipeline. At the start we ship four or five stages (validation, security, generation, post-processing) and add the rest when the data says it's time.
                    </p>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        Step 5. Pass a single context object through the pipeline
                    </h2>
                    <p>
                        We pass a single context object through the pipeline. The stages talk to each other through one container that travels the whole conveyor. Each stage reads what it needs and appends its own results without deleting anything. It's like blood moving through organs, picking something up and dropping something off at each one. By the end, the context holds the full history of how the request was processed, and for debugging that's priceless.
                    </p>
                    <p>
                        We make sure the full context can be saved and inspected at any stage. When something goes wrong, we open the snapshot and see: at step eight retrieval returned zero documents, routing picked the cheap model, and that's where the garbage answer came from. Without it you'd be guessing for hours, and on a client project that's straight lost time and money. A serializable context also matters because an agent can die mid-run: a crash, a timeout, a dropped connection. If there's nowhere to save the context, the user starts over from scratch.
                    </p>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        Step 6. Protect the context window and the KV cache
                    </h2>
                    <p>
                        We protect the context window and the KV cache. In a loop the agent appends the result of every tool call to the context. Over 10 iterations the context can grow tenfold, and the client pays for all of it on every step (a typical agent runs about 100 input tokens for every one output token). The KV cache is the rescue. If the start of the context didn't change between calls, the provider serves it from cache instead of recomputing, and the savings here aren't a few percent, they're multiples. That's why we treat cache hit rate as one of the key metrics of a production agent and watch it from day one.
                    </p>
                    <p>
                        Small things kill the cache, and we've been burned by them more than once. A changing timestamp at the top of the system prompt: one "harmless" line with the current time zeroes out the cache and makes calls ten times more expensive. Editing messages in the middle of the history. An inconsistent key order in JSON. Hence our rule: once the window is assembled and the agent is looping, we append to the end only. And if the context balloons past what fits, we rebuild it wholesale (compaction) rather than patching it piece by piece.
                    </p>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        Step 7. Wrap the agent loop in hard limits — in code, not in the prompt
                    </h2>
                    <p>
                        We wrap the agent loop in hard limits, in code, not in the prompt. The agent loop is the heart of the agent and, at the same time, the place where things break most often. It can fail to stop or ignore an explicit stop command (like that "code freeze" the Replit agent steamrolled), burn through budget (that $50 pilot that ballooned into an $847,000 production bill), or build a decision on a hallucination invented back on step three. So we set an iteration limit (25 is fine for a coding agent, around five for support before we hand off to a human), a token budget, a cap on repeated calls to the same tool, and human confirmation for irreversible or strange actions: placing an order, sending a payment, deleting data, an amount above the daily budget.
                    </p>
                    <p>
                        All of these limits live in our code. "Please stop after ten steps" in the prompt is just a wish the model is free to ignore. And when a limit trips, we don't answer the model with a bare "Error." We explain concretely: "this tool has been called the maximum number of times, you have enough data, give the final answer now." Then the model adapts instead of trying the same thing again.
                    </p>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        Step 8. Design tools more carefully than you pick the model
                    </h2>
                    <p>
                        We design tools more carefully than we pick the model. You can take the best model in the world and the agent will still make mistakes with poorly designed tools. For each tool we write a clear "verb + noun" name (the model uses it to decide whether the tool fits), a description written for the model rather than for a human (what it does, when to call it, what conditions must hold first), a strict parameter schema with default values (the tighter the schema, the fewer hallucinations), and, most importantly, meaningful errors. An error isn't a dead end, it's an instruction for the next step. Instead of "Something went wrong" we return "budget exceeded, current total $4,500 against a $4,000 limit, suggest reducing the order," and the model sees the specifics and proposes a way out on its own.
                    </p>
                    <p>
                        This matters especially when connecting tools over MCP. Automatic wrappers cover only the happy path, while production is made of timeouts, empty responses, and partial results. So before we wrap a service, we walk through every error it can return and prepare, for each one, a response the model will understand.
                    </p>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        Step 9. Build in observability from day one
                    </h2>
                    <p>
                        We build in observability from day one. Logging answers "what happened," while observability answers "why did this request cost 12 cents instead of two and take 8 seconds instead of one." Every meaningful pipeline event is visible to us: the start and end of a stage, errors, tool calls, loop iterations, model selection. We do this up front, not after the first incident, because after an incident the data you'd need to understand what happened is already gone.
                    </p>
                    <p>
                        All those failure stories (the million-dollar bills, the answers on a loop, the wiped production) happened not because the models were bad. There just wasn't a fail-fast pipeline, there were no limits in the loop, no well-thought-out tool errors, and no observability. So the next time a client gets pitched a "great framework," we tell them to ask: where are its limits, where are its meaningful tool errors, what about security? And if the answer is "the framework handles it," remember <b>terraform destroy</b>.
                    </p>
                    <p>
                        Thinking about putting an AI agent into production and want it to survive real traffic? <a href="/contacts">Get in touch</a> with our team and we'll help you build it on solid engineering, not magic.
                    </p>
                </div>
                <div>
                    <div class="footer-blog-item">
                        <div
                            class="btn-read-more primary contacts-button small"
                            id="show-modal"
                            @click="this.$router.push('/blog')"
                        >
                            <span class="button-content">
                                <span class="icon">
                                    <img
                                    v-svg-inline
                                    :data-src="require('@/assets/icons/rarr-icon.svg')"
                                    alt="" />
                                </span>
                                Read more our stories
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SidebarBlog from "./Sidebar.vue";
import Breadcrumbs from "/src/components/Breadcrumbs";
import {mapState} from "vuex";

export default {
    name: "BlogWhyAiAgentsBreak",
    data() {
        return {
            backHistory: history.state.back,
            tags:['AI Agents','AI-driven solutions','Business Automation','Custom Web Development','LLM','Production'],
            scroll: false,
            position: null,
            breadcrumbs: [
                {
                    link: '/',
                    name: 'Home',
                },
                {
                    name: 'Blog',
                    link: '/blog',
                },
                {
                    name: 'Why AI agents break in production',
                },
            ],
        };
    },
    components: {
        SidebarBlog,
        Breadcrumbs,
    },
    computed:{
        ...mapState(['blog'])
    },
    methods: {
        goToPath(tag){
            let post = this.blog.find(item => item.tags.includes(tag))
            this.$router.push(post.img_src)
        },
        back(){
            if(!document.referrer || this.backHistory !== '/blog'){
                Object.defineProperty(document, "referrer", {
                    get: function () {
                        return `${window.location.origin}/blog`;
                    },
                });
                this.$router.push('/blog')
                return
            } else{
                this.$router.go(-1)
            }
        },
        scrolling() {
            const scroll = document.documentElement.scrollTop;
            if (scroll > 50) {
                this.scroll = true;
            } else {
                this.scroll = false;
            }
        },
    },
    created() {
        window.history.scrollRestoration = "auto";
        window.addEventListener("scroll", this.scrolling);
    },
    beforeUnmount() {
        window.removeEventListener("scroll", this.scrolling);
    },
}
</script>

<style lang="scss">
@import "../../scss/_variables.scss";
@import "include-media/dist/_include-media.scss";
.article-blog-header {
    .article-blog-review__title {
        padding: 120px 0;
        text-align: end;
        text-transform: uppercase;
        color: $black;
        font-size: 32px;
        line-height: 111%;
        margin: 0;
        @include media(">=tablet") {
            font-size: 40px;
            max-width: 100%;
        }
        @include media(">=tablet-xl") {
            font-size: 56px;
        }
        @include media(">=widescreen") {
            font-size: 64px;
            max-width: 100%;
        }
    }
}

.footer-blog-item {
    display: block;
    padding-top: 20px;
    @include media(">=tablet") {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    .article-blog-tags {
        margin-top: 40px;
        color: $red;
        font-weight: 700;
        font-size: 16px;
        line-height: 28px;
        @include media(">=tablet") {
            margin-top: 0;
            text-align: center;
        }
        span {
            margin-right: 20px;
            @include media(">=widescreen") {
                margin-right: 40px;
            }
        }
    }
}

.article-blog-page {
    a {
        text-decoration: none;
        &:hover {
            text-decoration: underline;
        }
    }
    .link{
        color:$red;
        cursor:pointer;
        &:hover{
            text-decoration: underline;
        }
    }
    .btn-read-more {
        .button-content {
            border-radius: 26px;
            padding: 2px 12px 3px;
            background-color: $red;
            color: $white;
            font-weight: 700;
            font-size: 16px;
            line-height: 28px;
            cursor: pointer;
            width: max-content;
            background-image: linear-gradient($darkRed, $darkRed);
            background-repeat: no-repeat;
            transition: background-size 0s, color 0s;
            background-size: 0 100%;
            @include media(">=tablet-xl") {
                padding: 6px 24px 8px;
            }
            &:hover {
                background-size: 100% 100%;
                color: $white;
            }
        }
        .icon {
            padding-top: 6px;
            margin-right: 15px;
            transform: rotateY(180deg);
        }
        a {
            color: $white;
            text-decoration: none;
            &:hover {
                text-decoration: underline;
            }
        }
    }

    .go-back-to-read {
        padding: 16px 0;
        border-bottom-left-radius: 26px 26px;
        border-bottom-right-radius: 26px 26px;
        position: fixed;
        background: $white;
        z-index: 50;
        width: 100vw;
        transition: all 0.3s;
        @include media(">=tablet-xl") {
            padding: 16px 0;
        }
        &.scrolling {
            padding: 5px 0;
            border-bottom: 1px solid $red;
            @include media(">=tablet-xl") {
                padding: 5px 0;
            }
        }
    }
}
.article-blog {
    &.section_holder {
        display: flex;
        flex-direction: column-reverse;
        @include media(">=tablet-xl") {
            flex-direction: row;
        }
    }
    .section_title {
        margin-right: 0;
        width: 100%;
        @include media(">=tablet-xl") {
            margin-right: 43px;
            width: 35%;
        }
    }
    .section_content {
        margin-bottom: 60px;
        display: block;
        margin-left: 0;
        width: 100%;
        a {
            color: $red;
        }
        &__item {
            margin-bottom: 40px;
            @include media(">=tablet") {
                margin-bottom: 60px;
            }
            @include media(">=desktop") {
                margin-bottom: 120px;
            }
            &-title {
                &-icon {
                    flex-shrink: 0;
                    width: 400px;
                    height: auto;
                    margin-right: 15px;
                    @media all and (max-width: 480px) {
                        width: 100%;
                        margin-right: 0;
                    }
                    svg {
                        max-width: 100%;
                        width: 100%;
                        max-height: 100%;
                        height: 100%;
                    }
                }
                &--icon {
                    display: flex;
                    align-items: flex-end;
                    @media all and (max-width: 767px) {
                        flex-wrap: wrap;
                    }
                    @media (min-width: 992px) and (max-width: 1199px) {
                        flex-wrap: wrap;
                    }
                }
            }
            &-slider {
                position: relative;
            }
            &-link {
                display: inline-block;
                &:hover {
                    text-decoration: none;
                }
            }
        }
        @include media(">=tablet-xl") {
            margin-left: 37px;
            width: 65%;
            margin-bottom: 0;
        }
        &__paragraph {
            display: flex;
            margin-bottom: 45px;
            &-icon {
                flex-shrink: 0;
                display: inline-block;
                width: 65px;
                height: 65px;
                margin-right: 10px;
                vertical-align: middle;
                @media all and (min-width: 768px) {
                    width: 100px;
                    height: 100px;
                    margin-right: 15px;
                }
                svg {
                    max-width: 100%;
                    width: 100%;
                    max-height: 100%;
                    height: 100%;
                }
            }
        }
        &__list {
            &--two-columns {
                columns: 2;
                -webkit-columns: 2;
                -moz-columns: 2;
            }
            &--two-columns-tablet-desktop {
                @media all and (min-width: 768px) {
                    columns: 2;
                    -webkit-columns: 2;
                    -moz-columns: 2;
                }
            }
        }
    }
}

.article-blog__banner {
    position: relative;
    height: 300px;
    margin-bottom: 60px;
    @include media(">=tablet") {
        height: 500px;
    }
    @include media(">=desktop") {
        height: 600px;
        margin-bottom: 120px;
    }
    &--eclipse {
        z-index: 1;
        &:before {
            content: "";
            position: absolute;
            z-index: 2;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            background-color: rgba(0,0,0,0.3);
        }
        .article-blog__banner-title {
            z-index: 3;
        }
    }
    &-img,
    picture {
        display: block;
        height: 100%;
        img {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }
}
.article-blog__banner-title {
    text-transform: uppercase;
    color: $white;
    font-size: 32px;
    line-height: 111%;
    position: absolute;
    bottom: 20px;
    left: 16px;
    max-width: calc(100% - 32px);
    margin: 0;
    @include media(">=tablet") {
        bottom: 40px;
        font-size: 40px;
        left: 40px;
        max-width: calc(100% - 80px);
    }
    @include media(">=tablet-xl") {
        bottom: 80px;
        left: 40px;
        font-size: 56px;
        max-width: calc(100% - 80px);
    }
    @include media(">=widescreen") {
        bottom: 80px;
        left: 80px;
        font-size: 64px;
        max-width: 1150px;
    }
}
.article-blog__img,
picture {
    margin: 0 0 40px;
    display: block;
}
.article-blog__customer {
    em {
        display: block;
        margin-top: 32px;
    }
    .button {
        margin-top: 40px;
    }
}
</style>
